import { useState, useMemo, ReactNode } from "react"
import { VStack, HStack, Grid, GridItem, Text, Box, createListCollection } from "@chakra-ui/react"
import Dialog from "../../chakra/Dialog"
import AlertsList from "./AlertsList"
import { AlertWithSource } from "../../hooks/useAssetAlerts"
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText } from "@chakra-ui/react"
import { Link } from "../../components/Link"

interface AlertsDialogProps {
  alerts: AlertWithSource[]
  totalCount: number
  open: boolean
  onOpenChange: (open: boolean) => void
  assetId?: string
  availableAssets?: Array<{ id: string; name: string }>
  onAlertStatusUpdate?: (alertId: string, newStatus: string) => void
  refetchQueries?: string[]
  title?: string
  description?: string
  trigger?: ReactNode
  updateNote?: string
}

function AlertsDialog({
  alerts,
  totalCount,
  open,
  onOpenChange,
  assetId,
  availableAssets = [],
  onAlertStatusUpdate,
  refetchQueries = [],
  title = "Alerts",
  description,
  trigger,
  updateNote = "Updated from alerts dialog",
}: AlertsDialogProps) {
  const [selectedAssetId, setSelectedAssetId] = useState<string>("all")

  const mapHref = assetId ? `/alerts?asset=${assetId}` : "/alerts"

  const effectiveAssetFilter = useMemo(() => {
    if (availableAssets.length > 0) {
      return selectedAssetId
    }
    return assetId || "all"
  }, [selectedAssetId, assetId, availableAssets.length])

  const filteredAlerts = useMemo(() => {
    let filtered = [...alerts]

    if (effectiveAssetFilter !== "all") {
      filtered = filtered.filter(a => a.assetId === effectiveAssetFilter)
    }

    return filtered
  }, [alerts, effectiveAssetFilter])

  const systemAlerts = filteredAlerts.filter(a => a.source === "system")
  const assetAlerts = filteredAlerts.filter(a => a.source === "asset")

  const assetOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Assets" }]
    availableAssets.forEach(asset => {
      options.push({ value: asset.id, label: asset.name })
    })
    return createListCollection({ items: options })
  }, [availableAssets])

  const showSystemColumn = effectiveAssetFilter === "all"

  return (
    <Dialog
      title={title}
      description={description}
      open={open}
      setOpen={onOpenChange}
      trigger={trigger}
      size="xl"
      showCloseButton={true}
    >
      <VStack align="stretch" gap={4} width="100%">
        {availableAssets.length > 0 && (
          <Box minWidth="200px">
            <SelectRoot
              collection={assetOptions}
              value={[selectedAssetId]}
              onValueChange={(e) => setSelectedAssetId(e.value[0])}
              size="sm"
            >
              <SelectTrigger>
                <SelectValueText placeholder="All Assets" />
              </SelectTrigger>
              <SelectContent>
                {assetOptions.items.map((asset) => (
                  <SelectItem key={asset.value} item={asset}>
                    {asset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </Box>
        )}

        <Grid templateColumns={showSystemColumn ? "repeat(2, 1fr)" : "1fr"} gap={4} width="100%">
          {showSystemColumn && (
            <GridItem>
              <VStack align="stretch" gap={3}>
                <Link href={mapHref} fontSize="sm" fontWeight="semibold">
                  System Alerts ({systemAlerts.length})
                </Link>
                {systemAlerts.length > 0 ? (
                  <AlertsList
                    alerts={systemAlerts}
                    totalCount={systemAlerts.length}
                    onAlertStatusUpdate={onAlertStatusUpdate}
                    refetchQueries={refetchQueries}
                    maxHeight="400px"
                    updateNote={updateNote}
                    canResolveAlerts={false}
                  />
                ) : (
                  <Box p={4} textAlign="center" color="gray.500">
                    <Text fontSize="sm">No system alerts</Text>
                  </Box>
                )}
              </VStack>
            </GridItem>
          )}

          <GridItem>
            <VStack align="stretch" gap={3}>
              {showSystemColumn && (
                <Link href={mapHref} fontSize="sm" fontWeight="semibold">
                  Asset Alerts ({assetAlerts.length})
                </Link>
              )}
              {assetAlerts.length > 0 ? (
                <AlertsList
                  alerts={assetAlerts}
                  totalCount={assetAlerts.length}
                  onAlertStatusUpdate={onAlertStatusUpdate}
                  refetchQueries={refetchQueries}
                  maxHeight="400px"
                  updateNote={updateNote}
                  canResolveAlerts={false}
                />
              ) : (
                <Box p={4} textAlign="center" color="gray.500">
                  <Text fontSize="sm">No asset alerts</Text>
                </Box>
              )}
            </VStack>
          </GridItem>
        </Grid>

        {filteredAlerts.length === 0 && alerts.length > 0 && (
          <Box p={4} textAlign="center" color="gray.500">
            <Text>No alerts match the selected filters</Text>
          </Box>
        )}
      </VStack>
    </Dialog>
  )
}

export default AlertsDialog
